'use client';

import { useTouchPrimary } from '@/hooks/use-has-primary-touch';
import {
  getShortcut,
  isApplePlatform,
  resolveDisplayToken,
} from '@/lib/shortcuts';
import { cn } from '@/lib/utils';
import type { ComponentType, ReactNode } from 'react';

export function Kbd({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <kbd
      className={cn(
        'border-border/50 bg-muted/40 text-muted-foreground/70 pointer-events-none inline-flex h-5 min-w-5 items-center justify-center rounded border px-1 font-mono text-[10px] font-medium select-none',
        className
      )}
    >
      {children}
    </kbd>
  );
}

export function PrefixHint({
  prefix,
  label,
  onActivate,
}: {
  prefix: string;
  label: string;
  onActivate: (prefix: string) => void;
}) {
  return (
    <button
      type="button"
      tabIndex={-1}
      onMouseDown={(event) => {
        // Keep focus in the command input while applying the prefix.
        event.preventDefault();
        onActivate(prefix);
      }}
      className="text-muted-foreground/60 hover:text-foreground inline-flex items-center gap-1.5 text-[11px] transition-colors"
    >
      <Kbd className="pointer-events-auto cursor-pointer">{prefix}</Kbd>
      <span>{label}</span>
    </button>
  );
}

export function ItemIcon({
  icon: Icon,
  className,
  iconClassName,
}: {
  icon: ComponentType<{ className?: string }>;
  className?: string;
  iconClassName?: string;
}) {
  return (
    <span
      className={cn(
        'border-border/50 bg-muted/40 flex size-6 shrink-0 items-center justify-center rounded-md border',
        className
      )}
    >
      <Icon className={cn('size-3.5', iconClassName)} />
    </span>
  );
}

/** Kbd chips for a registered shortcut; hidden on touch devices. */
export function ShortcutHint({ shortcutId }: { shortcutId: string }) {
  const isTouch = useTouchPrimary();
  const shortcut = getShortcut(shortcutId);
  if (!shortcut || isTouch) return null;
  const apple = isApplePlatform();
  return (
    <span className="ml-auto hidden shrink-0 items-center gap-0.5 sm:inline-flex">
      {shortcut.display.map((token, index) => (
        <Kbd key={index}>{resolveDisplayToken(token, apple)}</Kbd>
      ))}
    </span>
  );
}

export type RelativeTimeTranslator = (
  key: 'justNow' | 'daysAgo' | 'hoursAgo' | 'minutesAgo',
  values?: { count: number }
) => string;

export function formatRelativeTime(
  timestamp: number,
  t: RelativeTimeTranslator
): string {
  const now = Date.now();
  const diff = now - timestamp;
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return t('daysAgo', { count: days });
  if (hours > 0) return t('hoursAgo', { count: hours });
  if (minutes > 0) return t('minutesAgo', { count: minutes });
  return t('justNow');
}
